import { useAtomValue } from 'jotai'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { omanCartHasSubscriptionAtom, omanFormatDataAtom, omanDeliveryFreightAtom } from '../../../stores/oman.store'
import { getHydraFreightAmount } from '../../../utils/oman-format.mapper'
import { OMAN_SHIPPING_METHODS, formatOmanShippingLabel } from '../../../utils/oman-shipping-methods'
import type { TOmanShippingMethodId } from '../../../types/oman.types'
import type { RadioProps } from '@mui/material/Radio'

const getRadioInputTestProps = (testId: string) =>
    ({
        'data-testid': testId
    }) as NonNullable<RadioProps['slotProps']>['input']

interface ChangeShippingMethodRadioProps {
    freight?: number
    disabled?: boolean
    selectedMethod: TOmanShippingMethodId
    onMethodChange: (method: TOmanShippingMethodId) => void
}

// Pure controlled radio group. The parent owns the draft selection and only
// commits it to global checkout state after the user clicks Confirm.
const ChangeShippingMethodRadio = ({ freight: freightProp, disabled = false, selectedMethod, onMethodChange }: ChangeShippingMethodRadioProps) => {
    const { translate } = useTranslate()
    const formatData = useAtomValue(omanFormatDataAtom)
    const deliveryFreight = useAtomValue(omanDeliveryFreightAtom)
    const hasSubscription = useAtomValue(omanCartHasSubscriptionAtom)
    const availableMethods = OMAN_SHIPPING_METHODS.filter(method => !(hasSubscription && method.id === 'pickup'))

    const freight = deliveryFreight || freightProp || getHydraFreightAmount(formatData) || formatData?.freight

    const handleSelect = (method: TOmanShippingMethodId) => {
        if (disabled || method === selectedMethod) return
        onMethodChange(method)
    }

    return (
        <FormControl
            component="fieldset"
            fullWidth
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                transition: 'opacity 0.2s'
            }}
            data-testid="change-shipping-method-radio-form-control">
            <RadioGroup
                value={selectedMethod}
                onChange={e => handleSelect(e.target.value as TOmanShippingMethodId)}
                sx={{ gap: '24px' }}
                data-testid="change-shipping-method-radio-radio-group">
                {availableMethods.map(method => {
                    const label = formatOmanShippingLabel({
                        id: method.id,
                        baseLabel: translate(method.labelKey),
                        freight,
                        freeLabel: translate('free')
                    })
                    return (
                        <MethodCard
                            key={method.id}
                            selected={selectedMethod === method.id}
                            onClick={() => handleSelect(method.id)}
                            data-testid={`change-shipping-method-radio-${method.id}-method-card`}>
                            <FormControlLabel
                                value={method.id}
                                control={
                                    <Radio
                                        size="small"
                                        sx={{ p: 0, mr: 2 }}
                                        slotProps={{
                                            input: getRadioInputTestProps(`change-shipping-method-radio-${method.id}-input`)
                                        }}
                                        data-testid={`change-shipping-method-radio-${method.id}-radio`}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: selectedMethod === method.id ? 700 : 400,
                                            color: selectedMethod === method.id ? '#003764' : 'rgb(102, 135, 162)'
                                        }}
                                        data-testid={`change-shipping-method-radio-${method.id}-label`}>
                                        {label}
                                    </Typography>
                                }
                                sx={{
                                    alignItems: 'center',
                                    m: 0,
                                    width: '100%'
                                }}
                                data-testid={`change-shipping-method-radio-${method.id}-form-control-label`}
                            />
                        </MethodCard>
                    )
                })}
            </RadioGroup>
        </FormControl>
    )
}

const MethodCard = styled(Box, {
    shouldForwardProp: p => p !== 'selected'
})<{ selected: boolean }>`
    padding: 15px 20px;
    cursor: pointer;
    border-radius: 8px;
    border: ${({ selected }) => (selected ? '2px solid #99BCDF' : '1px solid #CCDEEF')};
    background: ${({ selected }) => (selected ? '#F4F8FB' : '#FFFFFF')};
    transition:
        border-color 0.2s,
        background-color 0.2s;
`

export default ChangeShippingMethodRadio
